import type { OptionDB } from "@project/emerge";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type {
  RollerBoardProperties,
  RollerType,
} from "../interfaces/RollerBoardProperties";

function norm(s: string): string {
  return s.replaceAll(/\s/g, "");
}

/**
 * Legacy / partial JSON: missing `kind`, leftover `model`, or cutouts as {x,y,z} dict.
 */
function needsCatalogHydration(r: Record<string, unknown>): boolean {
  if (r.kind !== LTCaseUserOption.RollerType) return true;
  if (typeof r.model === "string" && r.model.length > 0) return true;
  const c = r.cutouts;
  if (
    c !== null &&
    c !== undefined &&
    typeof c === "object" &&
    !Array.isArray(c)
  )
    return true;
  return false;
}

function findRollerInCatalog(
  rollerType: ROLLER_TYPE,
  legacyName: string | undefined,
  all: RollerType[],
): RollerType | undefined {
  if (!legacyName) return undefined;
  const key = norm(legacyName);
  for (const candidate of all) {
    if (candidate.rollerType !== rollerType) continue;
    if (norm(candidate.name) === key) return candidate;
    if (candidate.aliases?.some((a) => norm(a) === key)) return candidate;
  }
  return undefined;
}

/**
 * After `migrateFlightCaseSettings`, replace imported exterior roller blobs (missing
 * `kind`, AH* names, legacy cutouts) with the matching OptionDB {@link RollerType}
 * row so `CornerBuildStep` gets `LTPartType.Roller` and `meshes.default` like new cases.
 *
 * Skips NONE and CREEPER (bundle + {@link hydrateImportedCreeperRollers}).
 */
export function hydrateImportedExteriorRollers(
  settings: FlightCaseSettings,
  optionDb: OptionDB,
): void {
  const rollers = settings.exteriorOptions?.rollers as
    | Record<string, unknown>
    | undefined;
  if (!rollers || typeof rollers !== "object") return;

  const rollerType = rollers.rollerType as ROLLER_TYPE | undefined;
  if (
    rollerType === undefined ||
    rollerType === ROLLER_TYPE.NONE ||
    rollerType === ROLLER_TYPE.CREEPER
  ) {
    return;
  }

  if (!needsCatalogHydration(rollers)) return;

  const legacyName = rollers.name as string | undefined;
  const all =
    optionDb.getAllOfKind<RollerType>(LTCaseUserOption.RollerType) ?? [];
  const catalog = findRollerInCatalog(rollerType, legacyName, all);
  if (!catalog) return;

  const boardFromImport = rollers.board as RollerBoardProperties | undefined;

  settings.exteriorOptions.rollers = {
    ...catalog,
    side: rollers.side ?? catalog.side,
    symmetry: rollers.symmetry ?? catalog.symmetry,
    board: boardFromImport ?? catalog.board,
    noBracketOffset: rollers.noBracketOffset ?? catalog.noBracketOffset,
    forceOrientation: rollers.forceOrientation ?? catalog.forceOrientation,
    applyFoamCutouts: rollers.applyFoamCutouts ?? catalog.applyFoamCutouts,
  } as FlightCaseSettings["exteriorOptions"]["rollers"];
}
